import { formatearMomento } from "@/lib/fechas";
import type {
  ArchivoDeAntecedente,
  CategoriaDocumento,
  DocumentoDelCatalogo,
  Obligatoriedad,
} from "@/lib/informes";

import { obligatoriedadDe, type DefinicionDeDocumentos } from "./definicionDeDocumentos";

/**
 * Las derivaciones de la pestaña de antecedentes (legales e históricos).
 *
 * Son puras y viven fuera del componente a propósito, igual que el cuadro de
 * construcciones: el estado de cada documento y los conteos de los filtros son
 * lo único de esta pantalla que puede estar mal sin que nada se vea roto —un
 * chip que dice 3 donde hay 4 se lee igual de bien—, y dentro del árbol un test
 * no podría separarlos del render.
 */

/**
 * En qué estado está UN documento.
 *
 * - `ok`: tiene al menos un archivo (sea obligatorio u opcional).
 * - `falta`: es obligatorio y no tiene ninguno. Es lo único que reclama.
 * - `opc`: es opcional y no tiene ninguno.
 * - `na`: se declaró que no corresponde. MANDA sobre todo lo demás, incluso
 *   sobre un archivo ya adjunto: lo que se declaró es que no se exige.
 */
export type EstadoDelDocumento = "ok" | "falta" | "opc" | "na";

export type FiltroDeDocumentos = "todos" | EstadoDelDocumento;

export function estadoDelDocumento(
  obligatoriedad: Obligatoriedad,
  cantidadDeArchivos: number,
): EstadoDelDocumento {
  if (obligatoriedad === "no_aplica") return "na";
  if (cantidadDeArchivos > 0) return "ok";

  return obligatoriedad === "obligatorio" ? "falta" : "opc";
}

/** Un documento del catálogo con todo lo que la pantalla necesita saber de él. */
export interface FilaDeDocumento {
  documento: DocumentoDelCatalogo;
  obligatoriedad: Obligatoriedad;
  archivos: ArchivoDeAntecedente[];
  estado: EstadoDelDocumento;
}

/**
 * Cruza el catálogo con lo elegido y con los archivos recibidos.
 *
 * Los archivos llegan PLANOS y de todas las categorías: se agrupan por
 * `documento_tipo_id` y solo cuentan los de documentos de este catálogo, que es
 * lo que hace que legales no muestre los históricos y viceversa.
 */
export function filasDeLosDocumentos(
  documentos: DocumentoDelCatalogo[],
  definicion: DefinicionDeDocumentos,
  archivos: ArchivoDeAntecedente[],
): FilaDeDocumento[] {
  const porDocumento = new Map<number, ArchivoDeAntecedente[]>();
  for (const archivo of archivos) {
    // Sin clasificar: no cuelga de ningún documento. Lo muestra la zona "Sin
    // clasificar" de la pestaña, no la lista del catálogo.
    if (archivo.documento_tipo_id === null) continue;

    const lista = porDocumento.get(archivo.documento_tipo_id);
    if (lista) lista.push(archivo);
    else porDocumento.set(archivo.documento_tipo_id, [archivo]);
  }

  return documentos.map((documento) => {
    const obligatoriedad = obligatoriedadDe(definicion, documento.id);
    const suyos = porDocumento.get(documento.id) ?? [];

    return {
      documento,
      obligatoriedad,
      archivos: suyos,
      estado: estadoDelDocumento(obligatoriedad, suyos.length),
    };
  });
}

type SeccionDelCliente = NonNullable<ArchivoDeAntecedente["seccion_cliente"]>;

/** Qué pestaña ve cada espacio del portal: lo legal va a legales, lo demás a históricos. */
const PESTANA_DE_LA_SECCION: Record<SeccionDelCliente, CategoriaDocumento> = {
  legal: "legal",
  tasaciones: "historico",
  descriptiva: "historico",
};

/**
 * Los archivos que el cliente mandó por el portal y nadie clasificó todavía,
 * los que le tocan a UNA pestaña.
 *
 * Sin `seccion_cliente` no hay a qué pestaña mandarlos: un archivo sin tipo
 * solo puede ser del cliente, así que ese caso no debería darse, y si se da no
 * se inventa un destino.
 */
export function archivosSinClasificar(
  archivos: ArchivoDeAntecedente[],
  categoria: CategoriaDocumento,
): ArchivoDeAntecedente[] {
  return archivos.filter(
    (a) =>
      a.documento_tipo_id === null
      && a.seccion_cliente !== null
      && PESTANA_DE_LA_SECCION[a.seccion_cliente] === categoria,
  );
}

/** El rótulo del espacio del portal por el que llegó el archivo, como lo vio el cliente. */
export function etiquetaDeLaSeccionDelCliente(seccion: SeccionDelCliente): string {
  return {
    legal: "Documentación legal",
    tasaciones: "Tasaciones anteriores",
    descriptiva: "Documentación descriptiva",
  }[seccion];
}

export interface ResumenDeObligatorios {
  obligatorios: number;
  conArchivo: number;
  /** 0–100. Sin obligatorios es 100: no hay nada pendiente. */
  porcentaje: number;
  completo: boolean;
}

/** "X de N obligatorios con archivo", y qué tan lleno va la barra. */
export function resumenDeObligatorios(filas: FilaDeDocumento[]): ResumenDeObligatorios {
  const obligatorios = filas.filter((f) => f.obligatoriedad === "obligatorio");
  const conArchivo = obligatorios.filter((f) => f.archivos.length > 0).length;

  return {
    obligatorios: obligatorios.length,
    conArchivo,
    porcentaje: obligatorios.length === 0 ? 100 : (conArchivo / obligatorios.length) * 100,
    completo: conArchivo === obligatorios.length,
  };
}

/** Cuántos documentos entran en cada filtro. Un filtro en cero sigue en el mapa. */
export function contarPorFiltro(filas: FilaDeDocumento[]): Record<FiltroDeDocumentos, number> {
  const conteo: Record<FiltroDeDocumentos, number> = { todos: filas.length, falta: 0, ok: 0, opc: 0, na: 0 };
  for (const fila of filas) conteo[fila.estado] += 1;

  return conteo;
}

export function filtrarFilas(filas: FilaDeDocumento[], filtro: FiltroDeDocumentos): FilaDeDocumento[] {
  return filtro === "todos" ? filas : filas.filter((f) => f.estado === filtro);
}

/** Los filtros en el orden del diseño, con su rótulo y el punto que los marca. */
export const FILTROS: { clave: FiltroDeDocumentos; rotulo: string; punto: "ok" | "falta" | "vacio" | null }[] = [
  { clave: "todos", rotulo: "Todos", punto: null },
  { clave: "falta", rotulo: "Faltan · obligatorios", punto: "falta" },
  { clave: "ok", rotulo: "Con archivos", punto: "ok" },
  { clave: "opc", rotulo: "Opcionales sin archivo", punto: "vacio" },
  { clave: "na", rotulo: "No aplica", punto: "vacio" },
];

/** La línea bajo el nombre del documento. */
export function subtituloDelDocumento(estado: EstadoDelDocumento, cantidadDeArchivos: number): string {
  if (estado === "na") return "No aplica a este predio";
  if (cantidadDeArchivos > 0) {
    return cantidadDeArchivos === 1 ? "1 archivo" : `${cantidadDeArchivos} archivos`;
  }

  return estado === "falta" ? "Falta adjuntar · obligatorio" : "Sin archivos · opcional";
}

export type OrigenDeArchivo = "cliente" | "web";

/**
 * De dónde vinieron los archivos de un documento, sin repetir y con el cliente
 * primero. `origen: null` cuenta como la web: es lo que el servidor deja
 * cuando no puede decir otra cosa, y el cliente SIEMPRE llega marcado.
 */
export function origenesDeLosArchivos(archivos: ArchivoDeAntecedente[]): OrigenDeArchivo[] {
  const hay = new Set(archivos.map((a): OrigenDeArchivo => (a.origen === "cliente" ? "cliente" : "web")));

  return (["cliente", "web"] as const).filter((o) => hay.has(o));
}

export function esImagen(nombre: string): boolean {
  return /\.(jpe?g|png|webp)$/i.test(nombre);
}

/** El día de subida, `dd-mm-aaaa` en hora de Chile; vacío si el servidor no lo dio. */
export function fechaDeSubida(subidoEn: string | null): string {
  return subidoEn ? formatearMomento(subidoEn) : "";
}
